/** Classic POS rPPG (Wang et al.) — local, no API. */

export type RppgEstimate = {
  heartRateBpm: number | null;
  /** Approximate RMSSD in ms from peak intervals; null if too few peaks */
  rmssdMs: number | null;
  confidence: number;
};

const BAND_LOW = 0.7; // ~42 bpm
const BAND_HIGH = 3.0; // ~180 bpm

function mean(xs: number[]): number {
  if (!xs.length) return 0;
  return xs.reduce((a, b) => a + b, 0) / xs.length;
}

function bandpassFft(signal: number[], fps: number): { filtered: number[]; dominantHz: number; power: number } {
  const n = signal.length;
  if (n < 32 || fps <= 0) {
    return { filtered: signal.slice(), dominantHz: 0, power: 0 };
  }

  // Remove DC / slow drift with moving average
  const win = Math.max(3, Math.round(fps * 1.2));
  const detrended = signal.map((_, i) => {
    const a = Math.max(0, i - Math.floor(win / 2));
    const b = Math.min(n, i + Math.ceil(win / 2));
    return signal[i] - mean(signal.slice(a, b));
  });

  // Naive DFT power in band — find dominant frequency
  let bestHz = 0;
  let bestPow = 0;
  const minK = Math.max(1, Math.floor((BAND_LOW * n) / fps));
  const maxK = Math.min(Math.floor(n / 2) - 1, Math.ceil((BAND_HIGH * n) / fps));

  for (let k = minK; k <= maxK; k++) {
    let re = 0;
    let im = 0;
    const omega = (2 * Math.PI * k) / n;
    for (let t = 0; t < n; t++) {
      re += detrended[t] * Math.cos(omega * t);
      im -= detrended[t] * Math.sin(omega * t);
    }
    const pow = re * re + im * im;
    if (pow > bestPow) {
      bestPow = pow;
      bestHz = (k * fps) / n;
    }
  }

  // Reconstruct single-tone filtered signal for peak picking
  const filtered = new Array(n).fill(0);
  if (bestHz > 0) {
    const k = Math.round((bestHz * n) / fps);
    let re = 0;
    let im = 0;
    const omega = (2 * Math.PI * k) / n;
    for (let t = 0; t < n; t++) {
      re += detrended[t] * Math.cos(omega * t);
      im -= detrended[t] * Math.sin(omega * t);
    }
    const amp = (2 / n) * Math.hypot(re, im);
    const phase = Math.atan2(im, re);
    for (let t = 0; t < n; t++) {
      filtered[t] = amp * Math.cos(omega * t + phase);
    }
  }

  const totalPow =
    detrended.reduce((s, v) => s + v * v, 0) / Math.max(1, n);
  const confidence = totalPow > 0 ? Math.min(1, bestPow / (totalPow * n * n * 0.25 + 1e-9)) : 0;

  return { filtered, dominantHz: bestHz, power: confidence };
}

function findPeakIntervalsMs(filtered: number[], fps: number): number[] {
  const intervals: number[] = [];
  if (filtered.length < 8 || fps <= 0) return intervals;

  let lastPeak = -1;
  for (let i = 1; i < filtered.length - 1; i++) {
    if (filtered[i] > filtered[i - 1] && filtered[i] >= filtered[i + 1] && filtered[i] > 0) {
      if (lastPeak >= 0) {
        const dtMs = ((i - lastPeak) / fps) * 1000;
        if (dtMs > 330 && dtMs < 1500) intervals.push(dtMs);
      }
      lastPeak = i;
    }
  }
  return intervals;
}

/**
 * POS projection on RGB means per frame.
 * `rgbSeries[i] = { r, g, b }` normalized 0–255 means of a skin ROI.
 */
export function estimateFromRgbSeries(
  rgbSeries: Array<{ r: number; g: number; b: number }>,
  durationSec: number,
): RppgEstimate {
  const n = rgbSeries.length;
  if (n < 48 || durationSec < 5) {
    return { heartRateBpm: null, rmssdMs: null, confidence: 0 };
  }

  const fps = n / durationSec;
  const rs = rgbSeries.map((x) => x.r);
  const gs = rgbSeries.map((x) => x.g);
  const bs = rgbSeries.map((x) => x.b);
  const rMean = mean(rs) || 1;
  const gMean = mean(gs) || 1;
  const bMean = mean(bs) || 1;

  // Temporal normalize
  const rn = rs.map((v) => v / rMean);
  const gn = gs.map((v) => v / gMean);
  const bn = bs.map((v) => v / bMean);

  // POS: S1 = Gn - Bn, S2 = Gn + Bn - 2Rn; combine with std ratio
  const s1 = gn.map((g, i) => g - bn[i]);
  const s2 = gn.map((g, i) => g + bn[i] - 2 * rn[i]);
  const std = (xs: number[]) => {
    const m = mean(xs);
    return Math.sqrt(mean(xs.map((v) => (v - m) ** 2)) || 1e-12);
  };
  const alpha = std(s1) / std(s2);
  const pos = s1.map((v, i) => v - alpha * s2[i]);

  const { filtered, dominantHz, power } = bandpassFft(pos, fps);
  const bpm = dominantHz > 0 ? Math.round(dominantHz * 60) : null;
  const intervals = findPeakIntervalsMs(filtered, fps);

  let rmssdMs: number | null = null;
  if (intervals.length >= 3) {
    let sumSq = 0;
    for (let i = 1; i < intervals.length; i++) {
      const d = intervals[i] - intervals[i - 1];
      sumSq += d * d;
    }
    rmssdMs = Math.round(Math.sqrt(sumSq / (intervals.length - 1)));
  }

  const heartRateBpm =
    bpm != null && bpm >= 45 && bpm <= 180 ? bpm : null;

  return {
    heartRateBpm,
    rmssdMs,
    confidence: heartRateBpm != null ? Math.min(1, power * 4) : 0,
  };
}

/** Average RGB in a centered forehead-ish ROI of an ImageData / canvas. */
export function sampleForeheadRgb(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
): { r: number; g: number; b: number } {
  const rw = Math.floor(width * 0.28);
  const rh = Math.floor(height * 0.14);
  const sx = Math.floor(width * 0.5 - rw / 2);
  const sy = Math.floor(height * 0.22);
  const data = ctx.getImageData(sx, sy, rw, rh).data;
  let r = 0;
  let g = 0;
  let b = 0;
  let count = 0;
  for (let i = 0; i < data.length; i += 4) {
    r += data[i];
    g += data[i + 1];
    b += data[i + 2];
    count += 1;
  }
  if (!count) return { r: 0, g: 0, b: 0 };
  return { r: r / count, g: g / count, b: b / count };
}

/** Runnable sanity check: `node -e` won't work (TS); call from console after import. */
export function demoRppgSelfCheck(): boolean {
  const fps = 30;
  const sec = 20;
  const hz = 1.2; // 72 bpm
  const series = Array.from({ length: fps * sec }, (_, i) => {
    const pulse = 2 * Math.sin((2 * Math.PI * hz * i) / fps);
    return { r: 120 + pulse, g: 90 + pulse * 1.4, b: 70 + pulse * 0.6 };
  });
  const est = estimateFromRgbSeries(series, sec);
  return est.heartRateBpm != null && Math.abs(est.heartRateBpm - 72) <= 6;
}